import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Proje {
    projeID: number;
    projeAdi: string;
}

interface StokErimeHiziGrup {
    grupAdi: string;
    toplamDaire: number;
    satilanDaire: number;
    satisOrani: number;
    ortalamaSatisSuresi: number | null;
}

interface StokErimeHiziRapor {
    gruplar: StokErimeHiziGrup[];
    toplamDaire: number;
    satilanDaire: number;
    genelSatisOrani: number;
}

const StokErimeHizi: React.FC = () => {
    const navigate = useNavigate();
    const [projeler, setProjeler] = useState<Proje[]>([]);
    const [selectedProjeID, setSelectedProjeID] = useState<string>('');
    const [groupByFeature, setGroupByFeature] = useState<string>('OdaSayisi');
    const [loading, setLoading] = useState(false);
    const [raporData, setRaporData] = useState<StokErimeHiziRapor | null>(null);

    useEffect(() => {
        fetchProjeler();
    }, []);

    const fetchProjeler = async () => {
        try {
            const response = await axiosInstance.get('/api/Projeler');
            setProjeler(response.data);
        } catch (error) {
            toast.error('Projeler yüklenirken hata oluştu');
        }
    };

    const fetchRapor = async () => {
        setLoading(true);
        try {
            const params: any = { groupByFeature };
            if (selectedProjeID) {
                params.projeID = selectedProjeID;
            }

            const response = await axiosInstance.get('/api/StokErimeHizi', { params });
            setRaporData(response.data);
        } catch (error: any) {
            toast.error(error?.response?.data?.message || 'Rapor yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleRaporOlustur = () => {
        fetchRapor();
    };

    const handleTemizle = () => {
        setSelectedProjeID('');
        setGroupByFeature('OdaSayisi');
        setRaporData(null);
    };

    // En hızlı ve en yavaş 5 özelliği belirle
    const enHizliGruplar = raporData
        ? [...raporData.gruplar]
              .filter(g => g.ortalamaSatisSuresi !== null)
              .sort((a, b) => (a.ortalamaSatisSuresi || 0) - (b.ortalamaSatisSuresi || 0))
              .slice(0, 5)
        : [];

    const enYavasGruplar = raporData
        ? [...raporData.gruplar]
              .filter(g => g.ortalamaSatisSuresi !== null)
              .sort((a, b) => (b.ortalamaSatisSuresi || 0) - (a.ortalamaSatisSuresi || 0))
              .slice(0, 5)
        : [];

    // Chart data - Satış Süresi
    const satirSuresiChartData = {
        labels: raporData?.gruplar.map(g => g.grupAdi) || [],
        datasets: [
            {
                label: 'Ortalama Satış Süresi (Gün)',
                data: raporData?.gruplar.map(g => g.ortalamaSatisSuresi || 0) || [],
                backgroundColor: 'rgba(14, 165, 233, 0.7)',
                borderColor: 'rgba(14, 165, 233, 1)',
                borderWidth: 1,
                borderRadius: 6,
            }
        ]
    };

    // Chart data - Satış Oranı
    const satisOraniChartData = {
        labels: raporData?.gruplar.map(g => g.grupAdi) || [],
        datasets: [
            {
                label: 'Satış Oranı (%)',
                data: raporData?.gruplar.map(g => g.satisOrani) || [],
                backgroundColor: 'rgba(34, 197, 94, 0.7)',
                borderColor: 'rgba(34, 197, 94, 1)',
                borderWidth: 1,
                borderRadius: 6,
            }
        ]
    };

    const satirSuresiChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    font: { size: 12, family: "'Inter', sans-serif" },
                    color: '#374151',
                    padding: 15,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleFont: { size: 13, family: "'Inter', sans-serif" },
                bodyFont: { size: 12, family: "'Inter', sans-serif" },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: function(context: any) {
                        const value = context.parsed.y;
                        return `Satış Süresi: ${value.toFixed(1)} gün`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Gün',
                    color: '#6b7280',
                    font: { size: 11, weight: 'bold' as const }
                },
                grid: {
                    color: '#f3f4f6',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: '#6b7280',
                }
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: '#6b7280',
                }
            }
        }
    };

    const satisOraniChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    font: { size: 12, family: "'Inter', sans-serif" },
                    color: '#374151',
                    padding: 15,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleFont: { size: 13, family: "'Inter', sans-serif" },
                bodyFont: { size: 12, family: "'Inter', sans-serif" },
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: function(context: any) {
                        const value = context.parsed.y;
                        return `Satış Oranı: ${value.toFixed(1)}%`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: '%',
                    color: '#6b7280',
                    font: { size: 11, weight: 'bold' as const }
                },
                grid: {
                    color: '#f3f4f6',
                    drawBorder: false,
                },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: '#6b7280',
                }
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: '#6b7280',
                }
            }
        }
    };

    const formatNumber = (num: number | null) => {
        if (num === null) return '—';
        return num.toFixed(1);
    };

    return (
        <div style={{ padding: '24px', fontFamily: "'Inter', 'Roboto', sans-serif", color: '#1f2937' }}>
            {/* Başlık */}
            <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <button
                        onClick={() => navigate('/proje-raporlari')}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#6b7280', fontSize: '20px', padding: '4px 8px',
                        }}
                        title="Raporlara Dön"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.025em' }}>
                        Stok Erime Hızı Raporu
                    </h1>
                </div>
                <p style={{ fontSize: '13.5px', color: '#6b7280', margin: 0, paddingLeft: '48px' }}>
                    Bağımsız bölümlerin satış hızını ve envanter회전 performansını analiz edin
                </p>
            </div>

            {/* Filtre Kartı */}
            <div style={{
                background: '#fff', borderRadius: '16px', padding: '24px',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                marginBottom: '24px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Filtreler</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    {/* Proje Seçimi */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '6px' }}>
                            Proje
                        </label>
                        <select
                            value={selectedProjeID}
                            onChange={e => setSelectedProjeID(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 12px', borderRadius: '8px',
                                border: '1.5px solid #e2e8f0', fontSize: '13px', color: '#374151',
                                outline: 'none', background: '#fff', cursor: 'pointer',
                            }}
                        >
                            <option value="">Tüm Projeler</option>
                            {projeler.map(p => (
                                <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>
                            ))}
                        </select>
                    </div>

                    {/* Analiz Kriteri */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4b5563', marginBottom: '6px' }}>
                            Analiz Kriteri
                        </label>
                        <select
                            value={groupByFeature}
                            onChange={e => setGroupByFeature(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 12px', borderRadius: '8px',
                                border: '1.5px solid #e2e8f0', fontSize: '13px', color: '#374151',
                                outline: 'none', background: '#fff', cursor: 'pointer',
                            }}
                        >
                            <option value="OdaSayisi">Oda Sayısına Göre</option>
                            <option value="Cephe">Cepheye Göre</option>
                            <option value="Manzara">Manzaraya Göre</option>
                            <option value="KatKonumu">Kat Konumuna Göre</option>
                            <option value="Blok">Bloka Göre</option>
                        </select>
                    </div>
                </div>

                {/* Butonlar */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        onClick={handleRaporOlustur}
                        disabled={loading}
                        style={{
                            padding: '10px 24px', borderRadius: '8px', border: 'none',
                            background: loading ? '#9ca3af' : '#0ea5e9', color: '#fff',
                            fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                        }}
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-spinner fa-spin"></i>
                                Yükleniyor...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-chart-bar"></i>
                                Rapor Oluştur
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleTemizle}
                        disabled={loading}
                        style={{
                            padding: '10px 24px', borderRadius: '8px',
                            border: '1.5px solid #e2e8f0', background: '#fff', color: '#6b7280',
                            fontSize: '13px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        <i className="fas fa-redo" style={{ marginRight: '6px' }}></i>
                        Temizle
                    </button>
                </div>
            </div>

            {/* Genel Özet Kartları */}
            {raporData && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <div style={{
                        background: '#fff', borderRadius: '12px', padding: '20px',
                        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>Toplam Daire</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#111827' }}>{raporData.toplamDaire}</div>
                    </div>
                    <div style={{
                        background: '#fff', borderRadius: '12px', padding: '20px',
                        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>Satılan Daire</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#22c55e' }}>{raporData.satilanDaire}</div>
                    </div>
                    <div style={{
                        background: '#fff', borderRadius: '12px', padding: '20px',
                        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>Genel Satış Oranı</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#0ea5e9' }}>
                            {formatNumber(raporData.genelSatisOrani)}%
                        </div>
                    </div>
                </div>
            )}

            {/* Grafikler */}
            {raporData && raporData.gruplar.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                    {/* Satış Süresi Grafiği */}
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '24px',
                        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '4px', height: '16px', background: '#0ea5e9', borderRadius: '2px' }}></div>
                            Ortalama Satış Süresi
                        </h3>
                        <div style={{ height: '350px' }}>
                            <Bar data={satirSuresiChartData} options={satirSuresiChartOptions} />
                        </div>
                    </div>

                    {/* Satış Oranı Grafiği */}
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '24px',
                        border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#111827', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '4px', height: '16px', background: '#22c55e', borderRadius: '2px' }}></div>
                            Satış Oranı
                        </h3>
                        <div style={{ height: '350px' }}>
                            <Bar data={satisOraniChartData} options={satisOraniChartOptions} />
                        </div>
                    </div>
                </div>
            )}

            {/* Tablolar */}
            {raporData && (enHizliGruplar.length > 0 || enYavasGruplar.length > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                    {/* En Hızlı Nakde Dönen */}
                    {enHizliGruplar.length > 0 && (
                        <div style={{
                            background: '#fff', borderRadius: '16px', padding: '24px',
                            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#16a34a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                                    <polyline points="17 6 23 6 23 12"/>
                                </svg>
                                En Hızlı Nakde Dönen 5 Özellik
                            </h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb', borderBottom: '1.5px solid #e5e7eb' }}>
                                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#4b5563' }}>Özellik</th>
                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#4b5563' }}>Satış Süresi</th>
                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#4b5563' }}>Satış Oranı</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {enHizliGruplar.map((grup, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '12px', color: '#374151', fontWeight: 500 }}>{grup.grupAdi}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>
                                                {formatNumber(grup.ortalamaSatisSuresi)} gün
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#6b7280' }}>
                                                {formatNumber(grup.satisOrani)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* En Yavaş Kalan */}
                    {enYavasGruplar.length > 0 && (
                        <div style={{
                            background: '#fff', borderRadius: '16px', padding: '24px',
                            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#dc2626', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
                                    <polyline points="17 18 23 18 23 12"/>
                                </svg>
                                En Yavaş Kalan 5 Özellik
                            </h3>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ background: '#f9fafb', borderBottom: '1.5px solid #e5e7eb' }}>
                                        <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#4b5563' }}>Özellik</th>
                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#4b5563' }}>Satış Süresi</th>
                                        <th style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#4b5563' }}>Satış Oranı</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {enYavasGruplar.map((grup, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '12px', color: '#374151', fontWeight: 500 }}>{grup.grupAdi}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>
                                                {formatNumber(grup.ortalamaSatisSuresi)} gün
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#6b7280' }}>
                                                {formatNumber(grup.satisOrani)}%
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Veri Yok Mesajı */}
            {!loading && !raporData && (
                <div style={{
                    background: '#fff', borderRadius: '16px', padding: '60px 24px',
                    border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    textAlign: 'center', color: '#9ca3af'
                }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 16px' }}>
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>
                        Raporu görmek için yukarıdaki filtreleri seçip "Rapor Oluştur" butonuna tıklayın.
                    </p>
                </div>
            )}
        </div>
    );
};

export default StokErimeHizi;
